import React, { useState, useRef, useCallback } from "react";
import * as Popover from "@radix-ui/react-popover";
import { MarkdownText } from "./MarkdownText";
import { Button } from "@/components/ui/button";

export interface PopoverAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: "default" | "outline" | "ghost";
  className?: string;
}

interface ItemDetailPopoverProps {
  item: {
    _key: string;
    name: string;
    icon: string;
    description?: string;
    points: number;
    item_type?: "addon" | "scene" | null;
  };
  /** Color theme for points: "danger" (punishments) or "success" (rewards) */
  pointsColor?: "danger" | "success";
  /** Prefix for points display, e.g. "+" or "-" */
  pointsPrefix?: string;
  actions?: PopoverAction[];
  children: React.ReactNode;
}

export function ItemDetailPopover({
  item,
  pointsColor = "danger",
  pointsPrefix = "+",
  actions,
  children,
}: ItemDetailPopoverProps) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleEnter = useCallback(() => {
    if (pinned) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(true), 300);
  }, [pinned]);

  const handleLeave = useCallback(() => {
    if (pinned) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  }, [pinned]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    clearTimeout(timeoutRef.current);
    setPinned(true);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setPinned(false);
    setOpen(false);
  }, []);

  const colorClass = pointsColor === "danger" ? "text-app-danger" : "text-app-success";
  const iconBg = item.item_type === "scene"
    ? "bg-app-info/10"
    : item.item_type === "addon"
    ? "bg-app-accent/10"
    : pointsColor === "danger"
    ? "bg-app-danger/10"
    : "bg-app-success/10";

  return (
    <Popover.Root open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <Popover.Trigger asChild>
        <span
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onClick={handleClick}
          className="cursor-pointer"
        >
          {children}
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          sideOffset={6}
          align="start"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onInteractOutside={() => { if (pinned) handleClose(); }}
          onClick={(e) => e.stopPropagation()}
          className={`z-50 rounded-xl border border-app-border bg-app-surface shadow-lg p-3 space-y-2 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ${
            pinned ? "w-md" : "w-80"
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`${pinned ? "w-10 h-10" : "w-9 h-9"} rounded-xl flex items-center justify-center text-lg ${iconBg}`}>
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`${pinned ? "text-body" : "text-body"} font-semibold`}>{item.name}</p>
              {item.item_type && (
                <span className={`inline-block text-info px-1.5 py-0 rounded-full font-medium ${
                  item.item_type === "scene" ? "bg-app-info/10 text-app-info" : "bg-app-accent/10 text-app-accent"
                }`}>
                  {item.item_type === "scene" ? "🎬 Scene" : "🧩 Addon"}
                </span>
              )}
            </div>
          </div>
          {item.description && (
            <MarkdownText
              text={item.description}
              className="text-info text-app-text-muted"
              truncateAtSplit={!pinned}
            />
          )}
          <p className={`text-info font-semibold ${colorClass}`}>
            {pointsPrefix}{item.points} pts
          </p>
          {pinned && actions && actions.length > 0 && (
            <div className="flex gap-2 pt-1 border-t border-app-border/50">
              {actions.map((action) => (
                <Button
                  key={action.label}
                  size="sm"
                  variant={action.variant ?? "outline"}
                  className={`flex-1 text-xs ${action.className ?? ""}`}
                  onClick={(e) => { e.stopPropagation(); action.onClick(); handleClose(); }}
                >
                  {action.icon}
                  {action.label}
                </Button>
              ))}
            </div>
          )}
          <Popover.Arrow className="fill-app-border" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
